import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { db, storage } from "@/lib/firebase";
import { collection, addDoc, query, where, getDocs, orderBy, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Upload, TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import FinancialCharts from "@/components/finance/FinancialCharts";

export type FinancialRow = {
  Date: string;
  Category: "Revenue" | "Expenses" | "Assets" | "Liabilities" | string;
  Value: number;
};

interface FinancialDataset {
  id: string;
  fileName: string;
  uploadDate: any;
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
}

export default function FinanceDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [rows, setRows] = useState<FinancialRow[]>([]);
  const [datasets, setDatasets] = useState<FinancialDataset[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeDataset, setActiveDataset] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    fetchDatasets();
  }, [user]);

  const fetchDatasets = async () => {
    if (!user) return;
    try {
      const q = query(
        collection(db, "financialData"),
        where("userId", "==", user.id)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as FinancialDataset[];
      
      // Sort manually since orderBy requires an index
      data.sort((a, b) => {
        const aDate = a.uploadDate?.toDate?.() || new Date(0);
        const bDate = b.uploadDate?.toDate?.() || new Date(0);
        return bDate.getTime() - aDate.getTime();
      });
      
      setDatasets(data);
    } catch (error) {
      console.error("Error fetching datasets:", error);
      toast({
        title: "Error",
        description: "Failed to fetch documents",
        variant: "destructive",
      });
    }
  };

  const parseNumber = (x: unknown): number => {
    if (typeof x === "number") return x;
    const s = String(x ?? "")
      .replace(/\u00A0/g, " ")
      .replace(/\s+/g, "")
      .replace(",", ".");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const parseDate = (d: string): Date => {
    const iso = new Date(d);
    if (!isNaN(iso.getTime())) return iso;

    const m = d.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (m) {
      const dd = Number(m[1]);
      const mm = Number(m[2]) - 1;
      const yyyy = Number(m[3].length === 2 ? "20" + m[3] : m[3]);
      const dt = new Date(yyyy, mm, dd);
      if (!isNaN(dt.getTime())) return dt;
    }

    return new Date(NaN);
  };

  const onFileUpload = async (file?: File) => {
    if (!file || !user) return;
    setLoading(true);

    try {
      const text = await file.text();
      const lines = text.split("\n").filter(l => l.trim());
      
      if (lines.length < 2) {
        throw new Error("File is empty or invalid");
      }

      const headers = lines[0].split(/[,;]/).map(h => h.trim());
      
      if (!headers.includes("Date") || !headers.includes("Value")) {
        throw new Error("Required columns missing: 'Date' and 'Value'");
      }

      const categoryIndex = headers.findIndex(h => 
        h === "Category" || h === "Catégorie" || h === "Categorie"
      );
      const dateIndex = headers.indexOf("Date");
      const valueIndex = headers.indexOf("Value") >= 0 
        ? headers.indexOf("Value") 
        : headers.findIndex(h => h === "Valeur");

      if (categoryIndex === -1 || dateIndex === -1 || valueIndex === -1) {
        throw new Error("Required columns missing");
      }

      const parsed: FinancialRow[] = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(/[,;]/).map(v => v.trim());
        if (values.length < 3) continue;

        const category = values[categoryIndex] || "";
        const normalizedCategory = 
          category === "Revenus" ? "Revenue" :
          category === "Dépenses" ? "Expenses" :
          category === "Actifs" ? "Assets" :
          category === "Passifs" ? "Liabilities" :
          category;

        parsed.push({
          Date: values[dateIndex],
          Category: normalizedCategory,
          Value: parseNumber(values[valueIndex]),
        });
      }

      parsed.sort((a, b) => Number(parseDate(a.Date)) - Number(parseDate(b.Date)));

      const totalRevenue = parsed
        .filter(r => r.Category === "Revenue")
        .reduce((sum, r) => sum + r.Value, 0);
      
      const totalExpenses = parsed
        .filter(r => r.Category === "Expenses")
        .reduce((sum, r) => sum + r.Value, 0);

      // Upload file to storage
      const storageRef = ref(storage, `financial/${user.id}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      // Save to Firestore
      const docRef = await addDoc(collection(db, "financialData"), {
        userId: user.id,
        fileName: file.name,
        fileUrl,
        data: parsed,
        totalRevenue,
        totalExpenses,
        netIncome: totalRevenue - totalExpenses,
        uploadDate: serverTimestamp(),
      });

      setRows(parsed);
      setActiveDataset(docRef.id);
      
      toast({
        title: "Success",
        description: `Imported ${parsed.length} records`,
      });

      fetchDatasets();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to parse CSV file",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDataset = async (datasetId: string) => {
    try {
      const snapshot = await getDocs(
        query(collection(db, "financialData"), where("__name__", "==", datasetId))
      );
      
      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        setRows(data.data || []);
        setActiveDataset(datasetId);
      }
    } catch (error) {
      console.error("Error loading dataset:", error);
    }
  };

  const stats = {
    revenue: rows.filter(r => r.Category === "Revenue").reduce((s, r) => s + r.Value, 0),
    expenses: rows.filter(r => r.Category === "Expenses").reduce((s, r) => s + r.Value, 0),
    assets: rows.filter(r => r.Category === "Assets").reduce((s, r) => s + r.Value, 0),
    liabilities: rows.filter(r => r.Category === "Liabilities").reduce((s, r) => s + r.Value, 0),
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Financial Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Upload CSV files with <span className="font-medium">Date, Category, Value</span> columns
          </p>
        </div>

        <div className="flex items-center gap-3">
          <label className="inline-block cursor-pointer">
            <Button disabled={loading}>
              <Upload className="mr-2 h-4 w-4" />
              {loading ? "Importing..." : "Import CSV File"}
            </Button>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => onFileUpload(e.target.files?.[0])}
            />
          </label>
        </div>

        {datasets.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Previous Uploads</CardTitle>
              <CardDescription>Select a dataset to view</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                {datasets.map((dataset) => (
                  <Button
                    key={dataset.id}
                    variant={activeDataset === dataset.id ? "default" : "outline"}
                    onClick={() => loadDataset(dataset.id)}
                    className="justify-start"
                  >
                    {dataset.fileName}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {rows.length > 0 && (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    ${stats.revenue.toLocaleString()}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
                  <TrendingDown className="h-4 w-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">
                    ${stats.expenses.toLocaleString()}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Net Income</CardTitle>
                  <DollarSign className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">
                    ${(stats.revenue - stats.expenses).toLocaleString()}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Net Assets</CardTitle>
                  <DollarSign className="h-4 w-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-purple-600">
                    ${(stats.assets - stats.liabilities).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            </div>

            <FinancialCharts rows={rows} />
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
